import { ErrorEvent, EventHint } from '@sentry/angular';

import { RecognizeFailedRequest } from './failed-request.model';

const ID_SEGMENT_REGEX = /^(?:\d+|[\da-f]{8,}(?:-[\da-f]{4,12}){0,4})$/i;
const ORIGIN_REGEX = /^(?:\w+:)*\/\/[^/]+/;
const QUERY_START_REGEX = /[?#]/;
const ROUTE_SEGMENT_REGEX = /^[a-z][a-z\d-]*$/i;

export function describeRequestError(
  event: ErrorEvent,
  hint: EventHint,
  recognizeFailedRequest: RecognizeFailedRequest,
): ErrorEvent {
  const failedRequest = recognize(hint, recognizeFailedRequest);
  const values = event.exception?.values ?? [];
  const exception = values.at(-1);

  if (!failedRequest || !exception) {
    return event;
  }

  const path = toPathTemplate(failedRequest.url);
  const errorCode = failedRequest.errorCode == null ? '' : `, errorCode ${failedRequest.errorCode}`;
  const request = [failedRequest.method, path, `${failedRequest.statusCode}`, failedRequest.status].filter(Boolean);
  const described = {
    ...exception,
    type: 'RequestFailure',
    value: `${request.join(' ')}${errorCode}`,
  };

  return {
    ...event,
    exception: { ...event.exception, values: [...values.slice(0, -1), described] },
    fingerprint: ['request-failure', failedRequest.method, path, `${failedRequest.statusCode}`, `${failedRequest.errorCode ?? ''}`],
  };
}

function recognize(hint: EventHint, recognizeFailedRequest: RecognizeFailedRequest): ReturnType<RecognizeFailedRequest> {
  try {
    return recognizeFailedRequest(hint.originalException);
  } catch {
    return undefined;
  }
}

function toPathTemplate(url: string | null | undefined): string {
  if (!url) {
    return '';
  }

  const segments = stripQuery(url.replace(ORIGIN_REGEX, ''))
    .split('/')
    .filter(Boolean)
    .map((segment) => ROUTE_SEGMENT_REGEX.test(segment) && !ID_SEGMENT_REGEX.test(segment) ? segment : '{id}');

  return segments.length === 0 ? '' : `/${segments.join('/')}`;
}

function stripQuery(path: string): string {
  const queryStart = path.search(QUERY_START_REGEX);

  return queryStart === -1 ? path : path.slice(0, queryStart);
}
